import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import geocodingClient from "@mapbox/mapbox-sdk/services/geocoding-v6";
import { sha256 } from "@/lib/sha256";
import type { ReactElement, ReactNode } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { IPINFO_ACCESS_TOKEN } from "@/lib/constants";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}


/**
 * Converts bytes to terabytes.
 * 
 * @param size - Size value in bytes.
 * @returns Size in terabytes with 3 places of precision.
 */
export function bToTB(size:number, wholeOnly?:boolean) : number { 
  const size_tb = size / (10 ** 12);
  if(wholeOnly) {
    return Math.floor(size_tb);
  }

  return parseFloat((size / (10 ** 12)).toFixed(2))
}

/**
 * Converts bytes to terabytes.
 * 
 * @param size - Size value in bytes.
 * @returns Size in terabytes with 3 places of precision.
 */
export function gbToTB(size:number) : number { 
  return size / 1000;
}

/**
 * Convert longitude/latitude pair into a placename.
 * @param longLat - Tuple pair of longitude and latitude numbers.
 * @param mapboxAccessToken - MapBox API Access Token
 * @param callback - Callback will be invoked with the translated placename (or an empty string)
 */
export function longlatToPlace(long: string, lat: string, mapboxAccessToken: string, callback: (place: string) => void) {
  const geocoding_client = geocodingClient({ accessToken: mapboxAccessToken });

  geocoding_client.reverseGeocode({
    longitude: parseFloat(long),
    latitude: parseFloat(lat)
  }).send()
    .then((resp:any) => {
      var found_loc_names: string[] = [];
      if (resp.body.features) {
        resp.body.features!.some((feature:any) => {
          if (feature.properties.feature_type == "place" && feature.properties.name_preferred) {
            found_loc_names.push(feature.properties.name_preferred)
          }
          if (feature.properties.feature_type == "country" && feature.properties.name_preferred) {
            found_loc_names.push(feature.properties.name_preferred)
          }
        })
      }
      callback(found_loc_names.join(", "))
    });
}

/**
 * Interpolate a format string with replacement values.
 * @param str - Fomrat string.
 * @param val - Replacement values.
 * @returns - Interpolated string.
 */
export function formatString(str: string, ...val: string[]) {
  for (let index = 0; index < val.length; index++) {
    str = str.replace(`{${index}}`, val[index]);
  }
  return str;
}

/**
 * Convert a timestamp in compact ISO8601 format (YYYYMMDDTHHMMSS[.sss]Z) to a Javascript Date object.
 * @param date 
 * @returns 
 */
export function parseCompactISO8601(ts:string):Date {
  var exp_ts:string = "";
  var seg_sz = 4;
  var base_idx = 0;
  var shift = 0;
  var sep = "-";
  var copy = true;
  
  for(var i=0;i<ts.length;i++) {
    if(ts[i] == "T") {
      sep = ":";
      seg_sz = 2;
      shift = 1;
      base_idx = i+1;
    }
    if(ts[i] == ".") {
      sep = "";
      copy = false;
    }
    if(ts[i] == "Z" || ts[i] == "-") {
      copy = true
    }
    if(copy) {
      if(i > base_idx && (i%seg_sz-shift) == 0) {
        exp_ts += sep+ts[i];
        seg_sz = 2;
      } else {
        exp_ts += ts[i];
      }
    }
  }
  
  return new Date(exp_ts);
}

/*
 * Convert a dictionary into a URL encoded string.
 */
export function urlEncodeData(data:any): string {
  const url_encoded_data_pairs:string[] = [];

  for(var name in data) {
    url_encoded_data_pairs.push(`${encodeURIComponent(name)}=${encodeURIComponent(data[name])}`);
  }

  return url_encoded_data_pairs.join('&').replace(/%20/g, '+');
}

/**
 * Decode jwt contents to a dictionary (does not validate the token).
 * @param jwt {string} - JWT to decode.
 */
export function jwtDecode(jwt:string):Record<string, any> {
  const payload = jwt.split(".")[1];
  if(typeof window === undefined) {
    return JSON.parse(Buffer.from(payload, 'base64').toString());
  } else {
    return JSON.parse(window.atob(payload));
  }
}

enum SuspenseStatus {pending, success, error};

/**
 * Makes an existing Promise React Suspense-compatible.
 * @param promise 
 * @returns `read` function compatible with the Suspense protocol.
 */
export function suspensify(promise:Promise<any>) {
  let status = SuspenseStatus.pending;
  let final_result:any;

  const susp_promise = promise.then(
    (res:any) => {
      status = SuspenseStatus.success;
      final_result = res;
    },
    (err:any) => {
      status = SuspenseStatus.error;
      final_result = err;
    }
  );

  return {
    read() {
      if (status === SuspenseStatus.pending) throw susp_promise;
      if (status === SuspenseStatus.error) throw final_result;
      return final_result;
    }
  };
}

/**
 * Compute the hash digest of the given text using the specified hash algorithm.
 * @param message Text to hash.
 * @param alg Hash algorithm to use. For supported values see: https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest
 * @returns Hex string representing hashed text.
 */
export function hashMessage(message:string, alg="SHA-256"):string {
  const encoder = new TextEncoder();
  const data = encoder.encode(message);
  const hash_buffer:Uint8Array<ArrayBuffer> = sha256(data) as Uint8Array<ArrayBuffer>;
  const byte_array = Array.from(hash_buffer);
  const hashHex = byte_array
    .map((b) => b.toString(16).padStart(2, "0"))
    .join(""); // convert bytes to hex string
  return hashHex;
}

/**
 * Compares two arrays to see if they are identical.
 * Credit: https://gist.github.com/Grubba27/25fea366c118f459f7af5e3b9e1dcc78
 * @param a 
 * @param b 
 * @returns 
 */
export function arraysEqual<T>(a: T[], b: T[]): boolean {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (a.length !== b.length) return false;

    for (var i = 0; i < a.length; ++i) {
        if (a[i] !== b[i]) return false;
    }
    return true;
}

/**
 * Debounces invocation of the supplied `callback` to occur at most every `delay` interval.
 * @param callback 
 * @param delay 
 * @returns 
 */
export function debounce<T extends unknown[]>(
  callback: (...args: T) => void,
  delay: number
) {
  let timer:ReturnType<typeof setTimeout>;

  return (...args: T) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      callback(...args);
    }, delay);
  }
}

/**
 * Convert IS8601 timestamp to human-friendly version (e.g. Today/Yesterday 4:30pm)
 * @param ts ISO8601 timestamp
 * @returns
 */
export function iso8601ToFriendlyDate(
  ts:string,
  today_label:string,
  yesterday_label:string
):string {
  return dateToFriendlyDate(new Date(ts), today_label, yesterday_label);
}

/**
 * Convert a Date object to human-friendly version (e.g. Today/Yesterday 4:30pm)
 * @param ts_date Date object
 * @returns
 */
export function dateToFriendlyDate(
  ts_date:Date,
  today_label:string,
  yesterday_label:string
):string {
  const today_date = new Date();
  const yesterday_date = new Date();
  yesterday_date.setDate(yesterday_date.getDate() - 1);

  var friendly_date = "";
  if (ts_date.toDateString() === today_date.toDateString()) {
    friendly_date = today_label;
  } else if (ts_date.toDateString() === yesterday_date.toDateString()) {
    friendly_date = yesterday_label;
  } else {
    friendly_date = ts_date.toLocaleDateString()
  }

  return `${friendly_date} ${ts_date.toLocaleString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true
  }).toLocaleLowerCase().replaceAll(' ', '')}`;
}

/**
 * Create a Mapbox compatible marker from a React element.
 */
export function mapMarker(node:ReactNode, height:number):HTMLElement {
  const marker = document.createElement("div");
  marker.className = "marker";
  marker.style.color = "var(--secondary-text-accent-light)";

  // Render synchronously so we can read the SVG's viewBox and size the
  // container width to match its aspect ratio.
  marker.innerHTML = renderToStaticMarkup(node as ReactElement);
  const svg = marker.querySelector("svg");
  const vb = svg?.viewBox.baseVal;
  const aspect = vb && vb.height > 0 ? vb.width / vb.height : 1;
  const width = height * aspect;

  marker.style.width = `${width}px`;
  marker.style.height = `${height}px`;
  if (svg) {
    svg.setAttribute("width", `${width}`);
    svg.setAttribute("height", `${height}`);

    // Clip the background fill to the icon's outer shape by cloning the
    // outermost drawable element and placing it behind the outlines, so
    // the container's rectangular corners don't bleed past rounded icon edges.
    const outer = svg.querySelector("rect, circle, ellipse, path");
    if (outer) {
      const bg = outer.cloneNode(true) as SVGElement;
      bg.setAttribute("style", "fill: var(--secondary-dark-violet); stroke: none; opacity: 1;");
      svg.insertBefore(bg, svg.firstChild);
    }
  }

  return marker;
}


/**
 * Return country code for the user's IP.
 * @returns ISO 3166 two-letter country code.
 */
export const visitorsCountryCode = async () => {
  try {
    const res = await fetch(`https://ipinfo.io/json?token=${IPINFO_ACCESS_TOKEN}`);
    if (!res.ok) { return "US"; }
    const data = await res.json();
    return typeof data?.country === "string" ? data.country : "US";
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(`GEOIP: ${error}`);
    return "US";
  }
};
